
import { PostgrestError } from '@supabase/supabase-js';
import { supabase } from './client';

/**
 * Execute a Supabase query with a timeout
 * @param queryFn Function that executes the Supabase query
 * @param timeoutMs Timeout in milliseconds
 * @returns Result of the query
 */
export async function executeWithTimeout<T>(
  queryFn: () => Promise<{ data: T | null; error: PostgrestError | null }>,
  timeoutMs: number = 5000
): Promise<{ data: T | null; error: PostgrestError | null }> {
  // Create a promise that rejects after timeoutMs
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Query timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    // Race the query and the timeout
    const result = await Promise.race([
      queryFn(),
      timeoutPromise
    ]) as { data: T | null; error: PostgrestError | null };
    
    return result;
  } catch (error) {
    console.error('Query execution error:', error);
    return { 
      data: null, 
      error: { 
        message: String(error), 
        details: '', 
        hint: '', 
        code: '500',
        name: 'QueryError' // Added missing 'name' property
      } 
    };
  }
}

/**
 * Execute a Supabase query with error handling
 * @param queryFn Function that executes the Supabase query
 * @returns Result of the query with better error handling
 */
export async function safeQueryExecution<T>(
  queryFn: () => Promise<{ data: T | null; error: PostgrestError | null }>,
): Promise<{ data: T | null; error: Error | null }> {
  try {
    const { data, error } = await queryFn();
    
    if (error) {
      console.error('Supabase query error:', error);
      return { data: null, error: new Error(error.message) };
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Exception during query execution:', error);
    return { data: null, error: error instanceof Error ? error : new Error(String(error)) };
  }
}

/**
 * Check if a table exists in the Supabase database
 * @param tableName Name of the table to check
 * @returns True if the table exists, false otherwise
 */
export async function checkTableExists(tableName: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('check_table_exists', { table_name: tableName });
    
    if (error) {
      console.error(`Error checking if table ${tableName} exists:`, error);
      return false;
    }
    
    return !!data;
  } catch (error) {
    console.error(`Exception checking if table ${tableName} exists:`, error);
    return false;
  }
}
